import React, { useCallback, useEffect, useState } from 'react';
import DialogLayout from '../DialogLayout';
import Typography from '../../components/Typography/Typography';
import ASSETS from '../../assets';
import {
  deviceLegacy,
  IDKitErrorCodes,
  IDKitRequestWidget,
  IDKitResult,
  orbLegacy,
  selfieCheckLegacy,
  type RpContext,
} from '@worldcoin/idkit';
import {
  getWorldIdRpContext,
  verifyWorldId,
} from '../../http/worldId';

const IDKIT_ERROR_MESSAGES: Partial<Record<IDKitErrorCodes, string>> = {
  [IDKitErrorCodes.VerificationRejected]:
    'Face verification failed. This scan does not match the stored credential.',
  [IDKitErrorCodes.IdentityAttributesNotMatched]:
    'Face verification failed. This scan does not match the stored credential.',
  [IDKitErrorCodes.UserRejected]: 'Verification was cancelled.',
  [IDKitErrorCodes.Cancelled]: 'Verification was cancelled.',
  [IDKitErrorCodes.FailedByHostApp]: 'Verification failed. Please try again.',
  [IDKitErrorCodes.Timeout]: 'Verification timed out. Please try again.',
  [IDKitErrorCodes.ConnectionFailed]:
    'Connection lost. Please check your connection and try again.',
  [IDKitErrorCodes.UserPresenceFailed]:
    'World App could not confirm your presence for this request.',
  [IDKitErrorCodes.UnexpectedResponse]:
    'Verification failed. Please try again.',
};

const formatVerificationError = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string') {
    return (
      IDKIT_ERROR_MESSAGES[error as IDKitErrorCodes] ??
      'Verification failed. Please try again.'
    );
  }
  return 'Verification failed. Please try again.';
};

interface HumanVerificationModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onBack?: () => void;
  onCloseAll?: () => void;
  app_id: `app_${string}`;
  action: string;
  signal: string;
  preset: "deviceLegacy" | "orbLegacy" | "selfieCheckLegacy";
  onVerificationSuccess?: (result: IDKitResult) => void;
  onVerificationError?: (error: any) => void;
}

const HumanVerificationModal: React.FC<HumanVerificationModalProps> = ({
  open,
  setOpen,
  onBack,
  onCloseAll,
  app_id,
  action,
  signal,
  preset,
  onVerificationSuccess,
  onVerificationError,
}) => {
  const [isLoadingRpContext, setIsLoadingRpContext] = useState(false);
  const [rpContext, setRpContext] = useState<RpContext | null>(null);
  const [widgetOpen, setWidgetOpen] = useState(true);
  const [rpContextError, setRpContextError] = useState<string | null>(null);

  const presetConfig =
    preset === "orbLegacy"
      ? orbLegacy({ signal })
      : preset === "selfieCheckLegacy"
        ? selfieCheckLegacy({ signal })
        : deviceLegacy({ signal });

  const handleCloseToVerificationMethod = () => {
    if (onBack) {
      onBack();
    } else {
      setOpen(false);
    }
  };

  const handleCloseAll = () => {
    if (onCloseAll) {
      onCloseAll();
    } else {
      setOpen(false);
    }
  };

  const handleBack = () => handleCloseToVerificationMethod();

  const handleVerificationFailure = (error: unknown) => {
    handleCloseAll();
    const message = formatVerificationError(error);
    onVerificationError?.(new Error(message));
  };

  const handleWidgetOpenChange = (isOpen: boolean) => {
    setWidgetOpen(isOpen);
    if (!isOpen) {
      handleCloseToVerificationMethod();
    }
  };

  const handleVerification = async (proof: IDKitResult) => {
    const verifyPayload = {
      proof,
      app_id,
      action,
      signal,
      rp_id: rpContext?.rp_id,
    };

    try {
      const result = await verifyWorldId(verifyPayload);

      console.log('Verification result:', result.data);
      onVerificationSuccess?.(proof);
      handleCloseAll();
    } catch (error: any) {
      const responseData = error?.response?.data;
      const message =
        responseData?.message ||
        error?.message ||
        'Verification failed';

      handleCloseAll();
      onVerificationError?.(new Error(message));

      throw error;
    }
  };

  const ensureRpContext = useCallback(async () => {
    setIsLoadingRpContext(true);
    setRpContextError(null);
    try {
      const response = await getWorldIdRpContext({ app_id, action });
      setRpContext(response.data.rp_context);
      return response.data.rp_context;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load verification session';
      setRpContextError(message);
      onVerificationError?.(new Error(message));
      throw error;
    } finally {
      setIsLoadingRpContext(false);
    }
  }, [app_id, action, onVerificationError]);

  useEffect(() => {
    if (!open) {
      setRpContext(null);
      setRpContextError(null);
      setWidgetOpen(true);
      return;
    }

    ensureRpContext().catch(() => undefined);
  }, [open, app_id, action, ensureRpContext]);

  const handleAppStoreClick = (url: string) => {
    window.open(url, "_blank");
  };

  const appStoreLinks = [
    {
      src: ASSETS.apple,
      alt: "App Store",
      url: "https://apps.apple.com/no/app/world-app-worldcoin-wallet/id1560859847?utm_source=chatgpt.com"
    },
    {
      src: ASSETS.google,
      alt: "Google Play",
      url: "https://play.google.com/store/apps/details?hl=en_US&id=com.worldcoin&utm_source=chatgpt.com"
    }
  ];

  return (
    <DialogLayout open={open}>
      <div className="w-full max-w-[532px] py-6 px-10 bg-[#F2F2F7] rounded-[15px] relative ">
      {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={handleBack}
            className="text-blue-600 font-bold text-sm hover:underline flex items-center gap-1"
          >
            <img src={ASSETS.arrow_left} alt="Back" className="w-3 h-3" />
            Back
          </button>
          <button
            onClick={handleCloseToVerificationMethod}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <img src={ASSETS.cross} alt="Close" className="w-4 h-4" />
          </button>
        </div>

        {/* Title */}
        <Typography variant="h2" className="font-bold title">
            Human Verification
          </Typography>
          
          <Typography variant="body1" color="grey" className="text-sm leading-relaxed">
            Open your World App on your mobile device and scan the QR code below to verify your identity.
          </Typography>

        {/* QR Code Section */}
        <div className="bg-white rounded-lg p-6 mt-5 mb-4">
          <div className="flex justify-center">
            {isLoadingRpContext ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <Typography variant="body2" color="grey">
                  Preparing QR code...
                </Typography>
              </div>
            ) : rpContextError ? (
              <div className="text-center py-8">
                <Typography variant="body2" color="grey" className="mb-4">
                  {rpContextError}
                </Typography>
                <button
                  onClick={() => {
                    setRpContext(null);
                    ensureRpContext().catch(() => undefined);
                  }}
                  className="text-blue-600 font-bold text-sm hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : rpContext ? (
              <IDKitRequestWidget
                open={widgetOpen}
                onOpenChange={handleWidgetOpenChange}
                app_id={app_id}
                action={action}
                rp_context={rpContext}
                environment="production"
                allow_legacy_proofs={true}
                preset={presetConfig}
                polling={{
                  interval: 2_000,
                  timeout: 120_000,
                }}
                onError={(errorCode: IDKitErrorCodes) => {
                  console.log('onError: ', errorCode);
                  handleVerificationFailure(errorCode);
                }}
                onSuccess={(response: IDKitResult) => {
                  console.log('onSuccess: ', response);
                }}
                handleVerify={handleVerification}
              />
            ) : null}
          </div>
          <div className="text-center mt-[162px] mb-[15px]">
          <Typography variant="body1" color="grey" className="text-sm mb-2 font-semibold">
            Don't have World ID?
          </Typography>
          <div className="flex justify-center gap-2">
            {appStoreLinks.map((link) => (
              <img
                key={link.alt}
                src={link.src}
                alt={link.alt}
                className="w-32 h-12 cursor-pointer"
                onClick={() => handleAppStoreClick(link.url)}
              />
            ))}
          </div>
        </div>
        </div>

        {/* Footer - Download World App */}
      
      </div>
    </DialogLayout>
  );
};

export default HumanVerificationModal;
